import { observer } from "mobx-react";
import RestAPI from "../../utils/restAPI";
import { makeAutoObservable } from "mobx";
import { useEffect, useState } from "react";
import {
	ComplementLineItemRulesMapNew,
	EDrywallWidthCalculation,
	IAlternativeMaximum,
	IComplementLineItemRule,
	IDatabaseLineItem,
	ITPARule,
	IXactimateAlternativeMapping,
	LineItemElementProps,
	PCWidthCalculation,
	PFWidthCalculation,
	TPARuleTypes,
} from "../../shared/common/EstimateEngine/EstimateEngineTypes";
import { appFeatures } from "../../shared/common/AppFeatures";
import { logDebug } from "../../shared/common/logger";
import SharedUtils from "../../shared/common/SharedUtils";
import { LineItemBuilder, LineItemBuilderMini } from "../components/EstimateEngine/EstimateTable/LineEditingDialog";

interface ITupleWithMaxQuantity {
	tupleID: string;
	maxQuantity: { [s: string]: number };
	lineItems: string[];
}


class LocalTPAStore {
	setAlternativeXAMappingsDest(index: number, value: string) {
		this._ruleDirty = true;
		this._alternativeXAMappings[index].dest = value;
	}
	setAlternativeXAMappingsSource(index: number, value: string) {
		this._ruleDirty = true;
		this._alternativeXAMappings[index].source = value;
	}
	private _alternativeXAMappings: IXactimateAlternativeMapping[] = [];
	get alternativeXAMappings() {
		return this._alternativeXAMappings;
	}
	set alternativeXAMappings(value) {
		this._ruleDirty = true;
		this._alternativeXAMappings = value;
	}

	setMaxQuantity(tupleID: string, measurement: string, value: number) {
		let elmIndex = this.alternativeMaximums.findIndex((a) => a.tupleID === tupleID);
		if (elmIndex === -1) {
			localTPAStore.alternativeMaximums.push({
				tupleID: tupleID,
				maxQuantity: value,
				explanation: "",
				measurement,
			});
		} else {
			this.alternativeMaximums[elmIndex].maxQuantity = value;
		}
		this._ruleDirty = true;
	}
	setExplanation(tupleID: string, explanation: string) {
		let elmIndex = this.alternativeMaximums.findIndex((a) => a.tupleID === tupleID);
		if (elmIndex === -1) {
			localTPAStore.alternativeMaximums.push({
				tupleID: tupleID,
				maxQuantity: 0,
				explanation,
				measurement: "",
			});
		} else {
			this.alternativeMaximums[elmIndex].explanation = explanation;
		}
		this._ruleDirty = true;
	}

	private _alternativeMaximums: IAlternativeMaximum[] = [];
	get alternativeMaximums() {
		return this._alternativeMaximums;
	}
	set alternativeMaximums(value) {
		this._ruleDirty = true;
		this._alternativeMaximums = value;
	}

	private _showFetchingAnimation: boolean = false;
	private _supportedSplitLineItemsRules: string[] = [];

	private _rollupType: string = "";
	private _rollupPercentage: number = 15;
	private _rollupApplied: string[] = [];
	private _allWithMaxQuantity: ITupleWithMaxQuantity[] = [];
	public get allWithMaxQuantity() {
		return this._allWithMaxQuantity;
	}
	public set allWithMaxQuantity(value) {
		this._allWithMaxQuantity = value;
	}

	public get rollupApplied() {
		return this._rollupApplied;
	}

	public set rollupApplied(value) {
		this._ruleDirty = true;
		this._rollupApplied = value;
	}

	public get rollupType() {
		return this._rollupType;
	}
	public set rollupType(value) {
		this._ruleDirty = true;
		this._rollupType = value;
	}
	public get rollupPercentage() {
		return this._rollupPercentage;
	}

	public set rollupPercentage(value) {
		this._ruleDirty = true;
		if (value < 0) {
			value = 0;
		}
		if (value > 100) {
			value = 100;
		}
		this._rollupPercentage = value;
	}

	get supportedSplitLineItemsRules() {
		return this._supportedSplitLineItemsRules;
	}
	set supportedSplitLineItemsRules(value) {
		this._supportedSplitLineItemsRules = value;
	}

	get showFetchingAnimation() {
		return this._showFetchingAnimation;
	}
	set showFetchingAnimation(value) {
		this._showFetchingAnimation = value;
	}

	importCarrier(): void {
		if (this.defaultCarrier === -1) {
			return;
		}
		if (!confirm("Are you sure you want to import carrier this will override the current rule type value ?")) {
			return;
		}
		const rule = this.perTpaAndCarrierRules[this.defaultCarrier];
		this.loadRule(rule);
		this._ruleDirty = true;
	}
	importTPA(): void {
		if (this.defaultTPA === -1) {
			return;
		}
		if (!confirm("Are you sure you want to import TPA this will override the current rule type value ?")) {
			return;
		}
		const rule = this.perTpaAndCarrierRules[this.defaultTPA];
		this.loadRule(rule);
		this._ruleDirty = true;
	}

	resetAll() {
		this.resetSelectedSplitLineItems();
		this.resetSelectedTuples();
		this.resetSelectedComplementLineItems();
		this.resetSelectedComplementLineItemsRules();
		this._dryWallPatchSize = 4;
		this._drywall1_2_5_8 = false;
		this._drywallFloodCuts = false;
		this._drywallPatchWithSize = false;
		this._drywall1_2_5_8_affected_material = [];
		this._drywall_continuous_surface = [];
		this._drywallFloodCuts_affected_material = [];
		this._drywallPatchWithSize_affected_material = [];
		this._baseboards = false;
		this._baseboards_affected_material = [];
		this._baseboards_continuous_surface = [];
		this._crownMolding = false;
		this._crownMolding_affected_material = [];
		this._crownMolding_continuous_surface = [];
		this._chairRail = false;
		this._chairRail_affected_material = [];
		this._chairRail_continuous_surface = [];
		this._trimBoard = false;
		this._trimBoard_affected_material = [];
		this._trimBoard_continuous_surface = [];
		this._selectedTuples = [];
		this._selectedComplementLineItems = [];
		this._selectedComplementLineItemsRules = [];
		this._selectedSplitLineItems = [];
		this._alternativeXAMappings = [];
		this._alternativeMaximums = [];
		this._drywallWidthType = EDrywallWidthCalculation.W
		this._baseboardsWidthType = PFWidthCalculation.PF
		this._chairRailWidthType = PCWidthCalculation.PC
		this._crownMoldingWidthType = PFWidthCalculation.PF
		this._trimBoardWidthType = PFWidthCalculation.PF
		this._conRoomToConLabType = ""
		this._rollupPercentage = 15;
		this._rollupType = "";
		this._expandedTupleIDS = [];
		this._expandedTupleID = "";
		this._ruleDirty = false;
	}
	loadRule(rule: ITPARule) {
		if (!this.checkBeforeContinueAndResetAll(false)) {
			return;
		}
		this.drywall1_2_5_8_affected_material = rule.rules.drywall1_2_5_8_affected_material ?? [];
		this.drywall_continuous_surface = rule.rules.drywall_continuous_surface ?? [];
		this.drywallFloodCuts_affected_material = rule.rules.drywallFloodCuts_affected_material ?? [];
		this.drywallPatchWithSize_affected_material = rule.rules.drywallPatchWithSize_affected_material ?? [];
		this._baseboards = rule.rules.baseboards ?? false;
		this._baseboards_affected_material = rule.rules.baseboards_affected_material ?? [];
		this._baseboards_continuous_surface = rule.rules.baseboards_continuous_surface ?? [];
		this._crownMolding = rule.rules.crownMolding ?? false;
		this._crownMolding_affected_material = rule.rules.crownMolding_affected_material ?? [];
		this._crownMolding_continuous_surface = rule.rules.crownMolding_continuous_surface ?? [];
		this._chairRail = rule.rules.chairRail ?? false;
		this._chairRail_affected_material = rule.rules.chairRail_affected_material ?? [];
		this._chairRail_continuous_surface = rule.rules.chairRail_continuous_surface ?? [];
		this._trimBoard = rule.rules.trimBoard ?? false;
		this._trimBoard_affected_material = rule.rules.trimBoard_affected_material ?? [];
		this._trimBoard_continuous_surface = rule.rules.trimBoard_continuous_surface ?? [];

		this._dryWallPatchSize = rule.rules.dryWallPatchSize ?? 4;
		this._drywall1_2_5_8 = rule.rules.drywall1_2_5_8 ?? false;
		this._drywallFloodCuts = rule.rules.drywallFloodCuts ?? false;
		this._drywallPatchWithSize = rule.rules.drywallPatchWithSize ?? false;
		this._alternativeXAMappings = rule.rules.alternativeXAMappings;
		this._alternativeMaximums = rule.rules.alternativeMaximums;
		this._rollupType = rule.rules.rollupType;
		this._rollupPercentage = rule.rules.rollupPercentage;
		this._drywallWidthType = rule.rules.drywallWidthType
		this._baseboardsWidthType = rule.rules.baseboardsWidthType ?? PFWidthCalculation.PF
		this._chairRailWidthType = rule.rules.chairRailWidthType ?? PCWidthCalculation.PC
		this._crownMoldingWidthType = rule.rules.crownMoldingWidthType ?? PFWidthCalculation.PF
		this._trimBoardWidthType = rule.rules.trimBoardWidthType ?? PFWidthCalculation.PF
		this._conRoomToConLabType = rule.rules.conRoomToConLabType ?? "CON_LAB";
		this._rollupApplied = rule.rules.rollupApplied;
		this._showRuleScreen = rule.ruleType;
		this._selectedTuples = rule.rules.selectedTuples;
		this._selectedComplementLineItems = rule.rules.selectedComplementLineItems;
		this._selectedComplementLineItemsRules = rule.rules.selectedComplementLineItemsRules;
		this._expandedTupleIDS = rule.rules.expandedTupleIDS;
		this._selectedSplitLineItems = rule.rules.splitLineItems;
		this._ruleDirty = false;
	}
	saveCurrentRule() {
		if (!this.ruleDirty) {
			this.newRuleType = "";
			return;
		}
		const newRule: ITPARule = {
			tpa: this.tpa,
			carrier: this.carrier,
			ruleType: this.newRuleType,
			rules: {
				drywall1_2_5_8_affected_material: this.drywall1_2_5_8_affected_material,
				drywallFloodCuts_affected_material: this.drywallFloodCuts_affected_material,
				drywallPatchWithSize_affected_material: this.drywallPatchWithSize_affected_material,
				drywall_continuous_surface: this.drywall_continuous_surface,
				dryWallPatchSize: this.dryWallPatchSize,
				drywall1_2_5_8: this.drywall1_2_5_8,
				drywallFloodCuts: this.drywallFloodCuts,
				drywallPatchWithSize: this.drywallPatchWithSize,
				drywallWidthType: this.drywallWidthType,
				baseboardsWidthType: this.baseboardsWidthType,
				chairRailWidthType: this.chairRailWidthType,
				crownMoldingWidthType: this.crownMoldingWidthType,
				trimBoardWidthType: this.trimBoardWidthType,
				baseboards: this.baseboards,
				baseboards_affected_material: this.baseboards_affected_material,
				baseboards_continuous_surface: this.baseboards_continuous_surface,
				crownMolding: this.crownMolding,
				crownMolding_affected_material: this.crownMolding_affected_material,
				crownMolding_continuous_surface: this.crownMolding_continuous_surface,
				chairRail: this.chairRail,
				chairRail_affected_material: this.chairRail_affected_material,
				chairRail_continuous_surface: this.chairRail_continuous_surface,
				trimBoard: this.trimBoard,
				trimBoard_affected_material: this.trimBoard_affected_material,
				trimBoard_continuous_surface: this.trimBoard_continuous_surface,
				conRoomToConLabType: this.conRoomToConLabType,
				alternativeXAMappings: this.alternativeXAMappings,
				alternativeMaximums: this.alternativeMaximums,
				rollupPercentage: this.rollupPercentage,
				rollupApplied: this.rollupApplied,
				rollupType: this.rollupType,
				selectedTuples: this.selectedTuples,
				selectedComplementLineItems: this.selectedComplementLineItems,
				selectedComplementLineItemsRules: this.selectedComplementLineItemsRules,
				expandedTupleIDS: this.expandedTupleIDS,
				splitLineItems: this.selectedSplitLineItems,
				rollupAppliedIDs: [],
			},
		};
		// find if there is a rule with the same type and replace it
		const index = this.perTpaAndCarrierRules.findIndex(
			(rule) => rule.tpa === this.tpa && rule.carrier === this.carrier && rule.ruleType === this.newRuleType,
		);
		if (index !== -1) {
			this.perTpaAndCarrierRules[index] = newRule;
		} else {
			this.perTpaAndCarrierRules = [...this.perTpaAndCarrierRules, newRule];
		}
		this.resetAll();
		this.newRuleType = "";
	}
	private _showRuleScreen: string =
		// appFeatures.isDevMachine ? "Split up Line Items" :
		"";
	get newRuleType() {
		return this._showRuleScreen;
	}
	set newRuleType(value) {
		if (!this.checkBeforeContinueAndResetAll(false)) {
			return;
		}
		this._showRuleScreen = value;
	}

	checkBeforeContinueAndResetAll(clearRule = true) {
		if (this.ruleDirty) {
			const yesNo = confirm(
				"You have unsaved changes, are you sure you want to continue without saving ( there is a save button on the top right ) ?",
			);
			if (!yesNo) {
				return false;
			}
		}
		this.resetAll();
		if (clearRule) {
			this.newRuleType = "";
		}
		setTimeout(() => {
			this.recommandImport();
		}, 50);
		return true;
	}
	recommandImport() {
		if (this.tpa && this.carrier && this.newRuleType) {
			this.defaultTPA = this.perTpaAndCarrierRules.findIndex(
				(rule) => !rule.tpa && rule.carrier === this.carrier && rule.ruleType === this.newRuleType,
			);
			this.defaultCarrier = this.perTpaAndCarrierRules.findIndex(
				(rule) => rule.tpa === this.tpa && !rule.carrier && rule.ruleType === this.newRuleType,
			);
		}
	}
	private _defaultTPA: number = -1;
	get defaultTPA() {
		return this._defaultTPA;
	}
	set defaultTPA(value) {
		this._defaultTPA = value;
	}
	private _defaultCarrier: number = -1;
	get defaultCarrier() {
		return this._defaultCarrier;
	}
	set defaultCarrier(value) {
		this._defaultCarrier = value;
	}
	private _drywall1_2_5_8_affected_material: string[] = [];
	private _drywall_continuous_surface: string[] = [];
	get drywall1_2_5_8_affected_material() {
		return this._drywall1_2_5_8_affected_material;
	}
	set drywall1_2_5_8_affected_material(value) {
		this._ruleDirty = true;
		this._drywall1_2_5_8_affected_material = value;
	}

	get drywall_continuous_surface() {
		return this._drywall_continuous_surface;
	}
	set drywall_continuous_surface(value) {
		this._ruleDirty = true;
		this._drywall_continuous_surface = value;
	}

	private _drywall1_2_5_8: boolean = false;
	get drywall1_2_5_8() {
		return this._drywall1_2_5_8;
	}
	set drywall1_2_5_8(value) {
		this._ruleDirty = true;
		this._drywall1_2_5_8 = value;
	}

	private _drywallFloodCuts_affected_material: string[] = [];
	get drywallFloodCuts_affected_material() {
		return this._drywallFloodCuts_affected_material;
	}
	set drywallFloodCuts_affected_material(value) {
		this._ruleDirty = true;
		this._drywallFloodCuts_affected_material = value;
	}

	private _drywallFloodCuts: boolean = false;
	get drywallFloodCuts() {
		return this._drywallFloodCuts;
	}
	set drywallFloodCuts(value) {
		this._ruleDirty = true;
		this._drywallFloodCuts = value;
	}


	private _drywallWidthType: EDrywallWidthCalculation = EDrywallWidthCalculation.W
	get drywallWidthType() {
		return this._drywallWidthType;
	}
	set drywallWidthType(value) {
		this._ruleDirty = true;
		this._drywallWidthType = value;
	}

	private _baseboardsWidthType: PFWidthCalculation = PFWidthCalculation.PF
	get baseboardsWidthType() {
		return this._baseboardsWidthType;
	}
	set baseboardsWidthType(value) {
		this._ruleDirty = true;
		this._baseboardsWidthType = value;
	}

	private _chairRailWidthType: PCWidthCalculation = PCWidthCalculation.PC
	get chairRailWidthType() {
		return this._chairRailWidthType;
	}
	set chairRailWidthType(value) {
		this._ruleDirty = true;
		this._chairRailWidthType = value;
	}

	private _crownMoldingWidthType: PFWidthCalculation = PFWidthCalculation.PF
	get crownMoldingWidthType() {
		return this._crownMoldingWidthType;
	}
	set crownMoldingWidthType(value) {
		this._ruleDirty = true;
		this._crownMoldingWidthType = value;
	}

	private _trimBoardWidthType: PFWidthCalculation = PFWidthCalculation.PF
	get trimBoardWidthType() {
		return this._trimBoardWidthType;
	}
	set trimBoardWidthType(value) {
		this._ruleDirty = true;
		this._trimBoardWidthType = value;
	}

	private _conRoomToConLabType: string = "CON_LAB";
	get conRoomToConLabType() {
		return this._conRoomToConLabType;
	}
	set conRoomToConLabType(value) {
		this._ruleDirty = true;
		this._conRoomToConLabType = value;
	}
	private _drywallPatchWithSize_affected_material: string[] = [];

	get drywallPatchWithSize_affected_material() {
		return this._drywallPatchWithSize_affected_material;
	}
	set drywallPatchWithSize_affected_material(value) {
		this._ruleDirty = true;
		this._drywallPatchWithSize_affected_material = value;
	}

	// Baseboards properties
	private _baseboards: boolean = false;
	get baseboards() {
		return this._baseboards;
	}
	set baseboards(value) {
		this._ruleDirty = true;
		this._baseboards = value;
	}

	private _baseboards_affected_material: string[] = [];
	get baseboards_affected_material() {
		return this._baseboards_affected_material;
	}
	set baseboards_affected_material(value) {
		this._ruleDirty = true;
		this._baseboards_affected_material = value;
	}

	private _baseboards_continuous_surface: string[] = [];
	get baseboards_continuous_surface() {
		return this._baseboards_continuous_surface;
	}
	set baseboards_continuous_surface(value) {
		this._ruleDirty = true;
		this._baseboards_continuous_surface = value;
	}

	// Crown Molding properties
	private _crownMolding: boolean = false;
	get crownMolding() {
		return this._crownMolding;
	}
	set crownMolding(value) {
		this._ruleDirty = true;
		this._crownMolding = value;
	}

	private _crownMolding_affected_material: string[] = [];
	get crownMolding_affected_material() {
		return this._crownMolding_affected_material;
	}
	set crownMolding_affected_material(value) {
		this._ruleDirty = true;
		this._crownMolding_affected_material = value;
	}

	private _crownMolding_continuous_surface: string[] = [];
	get crownMolding_continuous_surface() {
		return this._crownMolding_continuous_surface;
	}
	set crownMolding_continuous_surface(value) {
		this._ruleDirty = true;
		this._crownMolding_continuous_surface = value;
	}

	// Chair Rail properties
	private _chairRail: boolean = false;
	get chairRail() {
		return this._chairRail;
	}
	set chairRail(value) {
		this._ruleDirty = true;
		this._chairRail = value;
	}

	private _chairRail_affected_material: string[] = [];
	get chairRail_affected_material() {
		return this._chairRail_affected_material;
	}
	set chairRail_affected_material(value) {
		this._ruleDirty = true;
		this._chairRail_affected_material = value;
	}

	private _chairRail_continuous_surface: string[] = [];
	get chairRail_continuous_surface() {
		return this._chairRail_continuous_surface;
	}
	set chairRail_continuous_surface(value) {
		this._ruleDirty = true;
		this._chairRail_continuous_surface = value;
	}

	// Trim Board properties
	private _trimBoard: boolean = false;
	get trimBoard() {
		return this._trimBoard;
	}
	set trimBoard(value) {
		this._ruleDirty = true;
		this._trimBoard = value;
	}

	private _trimBoard_affected_material: string[] = [];
	get trimBoard_affected_material() {
		return this._trimBoard_affected_material;
	}
	set trimBoard_affected_material(value) {
		this._ruleDirty = true;
		this._trimBoard_affected_material = value;
	}

	private _trimBoard_continuous_surface: string[] = [];
	get trimBoard_continuous_surface() {
		return this._trimBoard_continuous_surface;
	}
	set trimBoard_continuous_surface(value) {
		this._ruleDirty = true;
		this._trimBoard_continuous_surface = value;
	}

	private _drywallPatchWithSize: boolean = false;
	get drywallPatchWithSize() {
		return this._drywallPatchWithSize;
	}
	set drywallPatchWithSize(value) {
		this._ruleDirty = true;
		this._drywallPatchWithSize = value;
	}
	private _dryWallPatchSize: number = 4;
	get dryWallPatchSize() {
		return this._dryWallPatchSize;
	}
	set dryWallPatchSize(value) {
		this._ruleDirty = true;
		if (value < 0) {
			value = 0;
		}
		this._dryWallPatchSize = value;
	}

	constructor() {
		makeAutoObservable(this);
	}
	private _showTPA = appFeatures.isDevMachineReal;
	get showTPA() {
		return this._showTPA;
	}
	set showTPA(value) {
		if (!value) {
			if (!this.checkBeforeContinueAndResetAll(false)) {
				return;
			}
			this._tpa = "";
			this._carrier = "";
			this._rule = "";
		}
		this._showTPA = value;
	}

	private _rule: string = "";
	get rule() {
		return this._rule;
	}
	set rule(value) {
		this._rule = value;
	}
	private _tpa: string =
		// appFeatures.isDevMachine ? "tpa1" :
		"";
	get tpa() {
		return this._tpa;
	}
	set tpa(value) {
		if (!this.checkBeforeContinueAndResetAll()) {
			return;
		}

		this._tpa = value;
	}
	private _carrier: string =
		// appFeatures.isDevMachine ? "c1" :
		"";
	get carrier() {
		return this._carrier;
	}
	set carrier(value) {
		if (!this.checkBeforeContinueAndResetAll()) {
			return;
		}
		this._carrier = value;
	}

	private _selectedCategory: any;
	private _search: string = "";
	private _selectedCell: any;
	get selectedCategory() {
		return this._selectedCategory;
	}
	set selectedCategory(value) {
		this._selectedCategory = value;
	}
	get search() {
		return this._search;
	}
	set search(value) {
		this._search = value;
	}
	get selectedCell() {
		return this._selectedCell;
	}
	set selectedCell(value) {
		this._selectedCell = value;
	}
	private _allActiveComplements: {
		tupleID: string;
		complementLineRules: ComplementLineItemRulesMapNew;
		selectedComplementLineItems: IDatabaseLineItem[];
		selectedComplementLineItemsID: string[];
	}[] = [];
	get allActiveComplements() {
		return this._allActiveComplements;
	}
	set allActiveComplements(value) {
		this._allActiveComplements = value;
	}
	private _databaseLineItems: IDatabaseLineItem[] = [];
	get databaseLineItems() {
		return this._databaseLineItems;
	}
	set databaseLineItems(value) {
		this._databaseLineItems = value;
	}
	async saveOrganizationTPARules(organizationId: string) {
		const data = await RestAPI.fetchAPI("/rest/access/saveOrganizationTPARules", "POST", {
			organizationTPARules: this.perTpaAndCarrierRules,
			organizationId,
		});
		return data;
	}
	async fetchComplementes() {
		if (this.allActiveComplements.length > 0) {
			return;
		}
		const data = await RestAPI.fetchAPI("/rest/access/allActiveComplements", "GET", {});
		if (data && data.allActiveComplements && data.allActiveComplements.length > 0) {
			this.allActiveComplements = data.allActiveComplements;
			// const _databaseLineItems: IDatabaseLineItem[] = [];
			// this.allActiveComplements.map((complement) => {

			// });
			// this.databaseLineItems = _databaseLineItems;
		}
		if (data && data.supportedSplitLineItemsRules && data.supportedSplitLineItemsRules.length > 0) {
			this.supportedSplitLineItemsRules = data.supportedSplitLineItemsRules;
		}
		if (data && data.allWithMaxQuantity && data.allWithMaxQuantity.length > 0) {
			this.allWithMaxQuantity = data.allWithMaxQuantity;
		}
		// if (data && data.databaseLineItems && data.databaseLineItems.length > 0) {
		// 	this.databaseLineItems = data.databaseLineItems;
		// }
	}
	getLineItemFromKey(key: string, selectedComplementLineItems: IDatabaseLineItem[]) {
		return selectedComplementLineItems.find((lineItem) => lineItem.id === key)?.originalLineItem;
	}

	private _expandedTupleID: string = "";
	get expandedTupleID() {
		return this._expandedTupleID;
	}
	set expandedTupleID(value) {
		this._expandedTupleID = value;
	}

	// remove complement data
	private _selectedTuples: string[] = [];
	get selectedTuples() {
		return this._selectedTuples;
	}
	set selectedTuples(value) {
		this._selectedTuples = value;
	}
	addSelectedTuple(value: string) {
		this.addToExpandedTupleIDS(value);
		// this.expandedTupleIDS = [...this.expandedTupleIDS, value];
		this._ruleDirty = true;
		this.selectedTuples = [...this.selectedTuples, value];
	}
	inSelectedTuples(value: string) {
		return this.selectedTuples.includes(value);
	}
	removeSelectedTuple(value: string) {
		this._expandedTupleIDS = this.expandedTupleIDS.filter((tuple) => tuple !== value);
		this._ruleDirty = true;
		this.selectedTuples = this.selectedTuples.filter((tuple) => tuple !== value);
	}
	resetSelectedTuples() {
		this.selectedTuples = [];
	}

	private _expandedTupleIDS: string[] = [];
	get expandedTupleIDS() {
		return this._expandedTupleIDS;
	}
	addToExpandedTupleIDS(value: string) {
		this._expandedTupleIDS.push(value);
		this._expandedTupleIDS = SharedUtils.removeDuplicateAndEmpty(this._expandedTupleIDS);
		logDebug("expandedTupleIDS", this._expandedTupleIDS);
	}

	private _selectedComplementLineItems: string[] = [];
	get selectedComplementLineItems() {
		return this._selectedComplementLineItems;
	}
	set selectedComplementLineItems(value) {
		this._selectedComplementLineItems = value;
	}
	createComplementKey(tuple: string, lineId: string) {
		return `${tuple}:${lineId}`;
	}
	addSelectedComplementLineItem(tuple: string, lineId: string) {
		this.addToExpandedTupleIDS(tuple);
		// this.expandedTupleIDS = [...this.expandedTupleIDS, tuple];
		this._ruleDirty = true;
		this.selectedComplementLineItems = [...this.selectedComplementLineItems, this.createComplementKey(tuple, lineId)];
	}
	inSelectedComplementLineItems(tuple: string, lineId: string) {
		return this.selectedComplementLineItems.includes(this.createComplementKey(tuple, lineId));
	}
	removeSelectedComplementLineItem(tuple: string, lineId: string) {
		this._ruleDirty = true;
		this.selectedComplementLineItems = this.selectedComplementLineItems.filter(
			(key) => key !== this.createComplementKey(tuple, lineId),
		);
	}
	resetSelectedComplementLineItems() {
		this.selectedComplementLineItems = [];
	}

	private _selectedSplitLineItems: string[] = [];
	get selectedSplitLineItems() {
		return this._selectedSplitLineItems;
	}
	set selectedSplitLineItems(value) {
		this._selectedSplitLineItems = value;
	}

	addSelectedSplitLineItem(value: string) {
		this._ruleDirty = true;
		this.selectedSplitLineItems = [...this.selectedSplitLineItems, value];
	}
	inSelectedSplitLineItems(value: string) {
		return this.selectedSplitLineItems.includes(value);
	}
	removeSelectedSplitLineItem(value: string) {
		this._ruleDirty = true;
		this.selectedSplitLineItems = this.selectedSplitLineItems.filter((key) => key !== value);
	}
	resetSelectedSplitLineItems() {
		this.selectedSplitLineItems = [];
	}

	private _selectedComplementLineItemsRules: string[] = [];
	get selectedComplementLineItemsRules() {
		return this._selectedComplementLineItemsRules;
	}
	set selectedComplementLineItemsRules(value) {
		this._selectedComplementLineItemsRules = value;
	}
	createComplementRuleKey(tuple: string, lineId: string, rule: IComplementLineItemRule) {
		const ex = (rule.exclusionSelectedVariabls ?? [])
			.map((variable) => `${variable.name}:${variable.index}`)
			.sort()
			.join(";");
		const target = (rule.targetVariables ?? [])
			.map((variable) => `${variable.name}:${variable.index}`)
			.sort()
			.join(";");
		const selected = (rule.selectedVariabls ?? [])
			.map((variable) => `${variable.name}:${variable.index}`)
			.sort()
			.join(";");
		return `${tuple}_${lineId}_${ex}_${target}_${selected}`;
	}
	addSelectedComplementLineItemRule(tuple: string, lineId: string, rule: IComplementLineItemRule) {
		this.addToExpandedTupleIDS(tuple);
		// this.expandedTupleIDS = [...this.expandedTupleIDS, tuple];
		this._ruleDirty = true;
		this.selectedComplementLineItemsRules = [
			...this.selectedComplementLineItemsRules,
			this.createComplementRuleKey(tuple, lineId, rule),
		];
	}
	inSelectedComplementLineItemsRules(tuple: string, lineId: string, rule: IComplementLineItemRule) {
		return this.selectedComplementLineItemsRules.includes(this.createComplementRuleKey(tuple, lineId, rule));
	}
	removeSelectedComplementLineItemRule(tuple: string, lineId: string, rule: IComplementLineItemRule) {
		this._ruleDirty = true;
		this.selectedComplementLineItemsRules = this.selectedComplementLineItemsRules.filter(
			(key) => key !== this.createComplementRuleKey(tuple, lineId, rule),
		);
	}
	resetSelectedComplementLineItemsRules() {
		this.selectedComplementLineItemsRules = [];
	}

	private ____ruleDirty: boolean = false;
	private get _ruleDirty() {
		return this.____ruleDirty;
	}
	private set _ruleDirty(value) {
		if (value) {
			logDebug(
				"dirty",
				new Error().stack
					?.split("\n")
					.filter((line) => line.includes("at set "))
					.join("\n"),
			);
		}
		this.____ruleDirty = value;
	}
	get ruleDirty() {
		return this._ruleDirty;
	}

	private _perTpaAndCarrierRules: ITPARule[] = [];
	get perTpaAndCarrierRules() {
		return this._perTpaAndCarrierRules;
	}
	set perTpaAndCarrierRules(value) {
		// Validate and clean rules for backward compatibility
		this._perTpaAndCarrierRules = this.validateAndCleanRules(value || []);
	}

	private validateAndCleanRules(rules: any[]): ITPARule[] {
		return rules.map(rule => {
			// Ensure rule has proper structure
			if (!rule || typeof rule !== 'object') {
				console.warn('Invalid rule detected, skipping:', rule);
				return null;
			}

			// Ensure rules object exists and is properly structured
			if (!rule.rules || typeof rule.rules !== 'object') {
				console.warn('Rule missing proper rules object, using defaults:', rule);
				rule.rules = {};
			}

			// Ensure all expected properties exist with defaults
			rule.rules = {
				drywall1_2_5_8_affected_material: rule.rules.drywall1_2_5_8_affected_material ?? [],
				drywallFloodCuts_affected_material: rule.rules.drywallFloodCuts_affected_material ?? [],
				drywallPatchWithSize_affected_material: rule.rules.drywallPatchWithSize_affected_material ?? [],
				drywall_continuous_surface: rule.rules.drywall_continuous_surface ?? [],
				dryWallPatchSize: rule.rules.dryWallPatchSize ?? 4,
				drywall1_2_5_8: rule.rules.drywall1_2_5_8 ?? false,
				drywallFloodCuts: rule.rules.drywallFloodCuts ?? false,
				drywallPatchWithSize: rule.rules.drywallPatchWithSize ?? false,
				drywallWidthType: rule.rules.drywallWidthType ?? EDrywallWidthCalculation.W,
				baseboardsWidthType: rule.rules.baseboardsWidthType ?? EDrywallWidthCalculation.W,
				chairRailWidthType: rule.rules.chairRailWidthType ?? EDrywallWidthCalculation.W,
				crownMoldingWidthType: rule.rules.crownMoldingWidthType ?? EDrywallWidthCalculation.W,
				trimBoardWidthType: rule.rules.trimBoardWidthType ?? EDrywallWidthCalculation.W,
				baseboards: rule.rules.baseboards ?? false,
				baseboards_affected_material: rule.rules.baseboards_affected_material ?? [],
				baseboards_continuous_surface: rule.rules.baseboards_continuous_surface ?? [],
				crownMolding: rule.rules.crownMolding ?? false,
				crownMolding_affected_material: rule.rules.crownMolding_affected_material ?? [],
				crownMolding_continuous_surface: rule.rules.crownMolding_continuous_surface ?? [],
				chairRail: rule.rules.chairRail ?? false,
				chairRail_affected_material: rule.rules.chairRail_affected_material ?? [],
				chairRail_continuous_surface: rule.rules.chairRail_continuous_surface ?? [],
				trimBoard: rule.rules.trimBoard ?? false,
				trimBoard_affected_material: rule.rules.trimBoard_affected_material ?? [],
				trimBoard_continuous_surface: rule.rules.trimBoard_continuous_surface ?? [],
				alternativeXAMappings: rule.rules.alternativeXAMappings ?? [],
				alternativeMaximums: rule.rules.alternativeMaximums ?? [],
				rollupType: rule.rules.rollupType ?? "",
				rollupPercentage: rule.rules.rollupPercentage ?? 15,
				rollupApplied: rule.rules.rollupApplied ?? [],
				rollupAppliedIDs: rule.rules.rollupAppliedIDs ?? [],
				selectedTuples: rule.rules.selectedTuples ?? [],
				selectedComplementLineItems: rule.rules.selectedComplementLineItems ?? [],
				selectedComplementLineItemsRules: rule.rules.selectedComplementLineItemsRules ?? [],
				expandedTupleIDS: rule.rules.expandedTupleIDS ?? [],
				splitLineItems: rule.rules.splitLineItems ?? [],
				...rule.rules
			};

			return rule as ITPARule;
		}).filter(rule => rule !== null) as ITPARule[];
	}
}

interface ComplementLineRule {
	exclusionSelectedVariabls: { name: string; index: number }[];
	targetVariables: { name: string; index: number }[];
	selectedVariabls: { name: string; index: number }[];
}

interface Complement {
	tupleID: string;
	complementLineRules: { [key: string]: ComplementLineRule[] };
}

const TPAComplementRules: React.FC<{}> = observer(() => {
	function checkIfComplementIsSuitable(complement: ComplementLineItemRulesMapNew, newRuleType: string): boolean {
		if (newRuleType === TPARuleTypes.RemoveComplement) {
			return true;
		}
		const allRules = Object.values(complement).flat();
		if (newRuleType === TPARuleTypes.ConvertToLikelyComplement) {
			return allRules.some((rule) => !(rule.isLikely ?? false));
		}
		if (newRuleType === TPARuleTypes.AdditionalComplements) {
			return allRules.some((rule) => rule.isSpecialTPAActivationComplements);
		}
		return false;
	}
	function checkIfRuleIsSuitable(rule: IComplementLineItemRule, newRuleType: string) {
		if (newRuleType === TPARuleTypes.RemoveComplement) {
			return true;
		}
		if (newRuleType === TPARuleTypes.ConvertToLikelyComplement) {
			return !rule.isLikely;
		}
		if (newRuleType === TPARuleTypes.AdditionalComplements) {
			return rule.isSpecialTPAActivationComplements;
		}
	}

	return (
		<div className="h-full overflow-auto">
			<div>
				{localTPAStore.allActiveComplements.map((complement, index) => {
					if (!checkIfComplementIsSuitable(complement.complementLineRules, localTPAStore.newRuleType)) {
						return null;
					}
					return (
						<div key={index}>
							<div className="flex items-center m-1">
								<input
									type="checkbox"
									className="m-1"
									checked={localTPAStore.inSelectedTuples(complement.tupleID)}
									onChange={() => {
										if (localTPAStore.inSelectedTuples(complement.tupleID)) {
											localTPAStore.removeSelectedTuple(complement.tupleID);
										} else {
											localTPAStore.addSelectedTuple(complement.tupleID);
										}
									}}
								/>
								<div
									onClick={() => {
										if (localTPAStore.expandedTupleID === complement.tupleID) {
											localTPAStore.expandedTupleID = "";
										} else {
											localTPAStore.expandedTupleID = complement.tupleID;
										}
									}}
									className="btn"
									key={index}
								>
									{complement.tupleID}
								</div>
							</div>
							{(localTPAStore.expandedTupleID === complement.tupleID ||
								localTPAStore.expandedTupleIDS.includes(complement.tupleID)) && (
									<div className="ml-4 mb-4">
										{Object.keys(complement.complementLineRules).map((key) => {
											return (
												<div>
													<div key={key} className="flex items-center">
														<input
															type="checkbox"
															className="m-1"
															checked={localTPAStore.inSelectedComplementLineItems(complement.tupleID, key)}
															onChange={() => {
																if (localTPAStore.inSelectedComplementLineItems(complement.tupleID, key)) {
																	localTPAStore.removeSelectedComplementLineItem(complement.tupleID, key);
																} else {
																	localTPAStore.addSelectedComplementLineItem(complement.tupleID, key);
																}
															}}
														/>
														{localTPAStore.getLineItemFromKey(key, complement.selectedComplementLineItems)}
													</div>
													<div>
														{complement.complementLineRules[key].map((rule, index) => {
															if (!checkIfRuleIsSuitable(rule, localTPAStore.newRuleType)) {
																return null;
															}
															return (
																<div className="ml-8 flex items-start">
																	{complement.complementLineRules[key].length > 1 && (
																		<input
																			type="checkbox"
																			className="m-1"
																			checked={localTPAStore.inSelectedComplementLineItemsRules(
																				complement.tupleID,
																				key,
																				rule,
																			)}
																			onChange={() => {
																				if (
																					localTPAStore.inSelectedComplementLineItemsRules(
																						complement.tupleID,
																						key,
																						rule,
																					)
																				) {
																					localTPAStore.removeSelectedComplementLineItemRule(
																						complement.tupleID,
																						key,
																						rule,
																					);
																				} else {
																					localTPAStore.addSelectedComplementLineItemRule(
																						complement.tupleID,
																						key,
																						rule,
																					);
																				}
																			}}
																		/>
																	)}
																	<div key={index} className="ml-4">
																		{(rule.exclusionSelectedVariabls?.length ?? 0) > 0 && (
																			<div>
																				<div>Exclusion Selected Variabls</div>
																				{rule.exclusionSelectedVariabls?.map((variable, index) => {
																					return (
																						<div key={index} className="ml-4">
																							{variable.name}
																						</div>
																					);
																				})}
																			</div>
																		)}
																		{(rule.targetVariables?.length ?? 0) > 0 && (
																			<div>
																				<div>Target Variables</div>
																				{rule.targetVariables!.map((variable, index) => {
																					return (
																						<div key={index} className="ml-4">
																							{variable.name}
																						</div>
																					);
																				})}
																			</div>
																		)}
																		{(rule.selectedVariabls?.length ?? 0) > 0 && (
																			<div>
																				<div>Selected Variabls</div>
																				{rule.selectedVariabls!.map((variable, index) => {
																					return (
																						<div key={index} className="ml-4">
																							{variable.name}
																						</div>
																					);
																				})}
																			</div>
																		)}
																		{(rule.selectedVariabls?.length ?? 0) === 0 &&
																			(rule.targetVariables?.length ?? 0) === 0 &&
																			(rule.exclusionSelectedVariabls?.length ?? 0) === 0 && (
																				<div>Default settings</div>
																			)}
																	</div>
																</div>
															);
														})}
													</div>
												</div>
											);
										})}
									</div>
								)}
						</div>
					);
				})}
			</div>
		</div>
	);
});

const SplitUpLineItemsScreen: React.FC<{}> = observer(() => {
	const [lineItemsElements, setLineItemsElements] = useState<
		{
			elements: LineItemElementProps[];
			lineItemId: string;
		}[]
	>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [loadingProgress, setLoadingProgress] = useState<{ completed: number, total: number }>({ completed: 0, total: 5 });

	async function getElements(tid: string): Promise<{ elements: LineItemElementProps[]; lineItemId: string }[]> {
		try {
			const url = RestAPI.getServerBaseUrl(`/rest/estimates/getLineItemElements?lineItemId=${tid}`);
			const elements = (await RestAPI.fetchURL(url, "GET")) as {
				elements: LineItemElementProps[];
				lineItemId: string;
			}[];
			// logDebug("elements", tid, elements);
			await SharedUtils.sleep(200);
			return elements;
		} catch (err) {
			return [];
		}
	}

	async function fetchAllElements() {
		setIsLoading(true);
		setLoadingProgress({ completed: 0, total: 5 });

		const lineItemIds = [
			"1c066ee7-afe1-430f-ad11-3b9ad90aa7a4", // paint line item
			"1b33d4fd-3724-46d1-8594-ea0f2a3297e4", // baseboard oversized
			"f2cf2ed1-9bc3-46e5-b8cb-0b0526e4fa8f", // crown paint oversized
			"0939410c-f5a7-4c8b-82f0-31e592932aad", // Chair rail oversized	
			"c8546855-a618-4fb2-91c9-d9835c84be0b"  // trim paint one coat
		];

		try {
			// Fetch all elements concurrently
			const promises = lineItemIds.map((id, index) =>
				getElements(id).then(result => {
					setLoadingProgress(prev => ({ ...prev, completed: prev.completed + 1 }));
					return result;
				})
			);

			const results = await Promise.all(promises);

			// Flatten and set all elements at once
			const allElements = results.flat();
			setLineItemsElements(allElements);
		} catch (error) {
			console.error("Error fetching line item elements:", error);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		fetchAllElements();
	}, []);
	const drywall_1w_2p = localTPAStore.supportedSplitLineItemsRules.find((rule) => rule.includes("1w_2p"));

	const updateDrywallWidthType = (value: EDrywallWidthCalculation) => {
		localTPAStore.drywallWidthType = value;
	}

	const updateBaseboardsWidthType = (value: PFWidthCalculation) => {
		localTPAStore.baseboardsWidthType = value;
	}

	const updateChairRailWidthType = (value: PCWidthCalculation) => {
		localTPAStore.chairRailWidthType = value;
	}

	const updateCrownMoldingWidthType = (value: PFWidthCalculation) => {
		localTPAStore.crownMoldingWidthType = value;
	}

	const updateTrimBoardWidthType = (value: PFWidthCalculation) => {
		localTPAStore.trimBoardWidthType = value;
	}
	return (
		<div className="mb-8">
			{localTPAStore.supportedSplitLineItemsRules.map((rule, index) => {
				return (
					<div className="flex m-1" key={index}>
						<input
							checked={localTPAStore.selectedSplitLineItems.includes(rule)}
							onChange={() => {
								if (localTPAStore.inSelectedSplitLineItems(rule)) {
									localTPAStore.removeSelectedSplitLineItem(rule);
								} else {
									localTPAStore.addSelectedSplitLineItem(rule);
								}
							}}
							type="checkbox"
							className="mx-1"
						/>
						<div>{rule}</div>
					</div>
				);
			})}

			{/* Loading State */}
			{isLoading && localTPAStore.selectedSplitLineItems.includes(drywall_1w_2p ?? "") && (
				<div className="ml-6 flex flex-col items-center justify-center h-[400px] mb-8">
					<div className="text-lg mb-4">Loading line item elements...</div>
					<div className="w-64 bg-gray-200 rounded-full h-2.5">
						<div
							className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
							style={{ width: `${(loadingProgress.completed / loadingProgress.total) * 100}%` }}
						></div>
					</div>
					<div className="text-sm text-gray-600 mt-2">
						{loadingProgress.completed} of {loadingProgress.total} completed
					</div>
				</div>
			)}

			{/* Split Up Line Items Content - Only show when not loading and all data is loaded */}
			{!isLoading &&
				localTPAStore.selectedSplitLineItems.includes(drywall_1w_2p ?? "") &&
				lineItemsElements.length >= 5 &&
				lineItemsElements.every(item => item.elements.length > 0) && (
					<div className="ml-6 flex flex-col h-[400px] overflow-auto mb-8">
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1  justify-start "
								checked={localTPAStore.drywall1_2_5_8}
								onChange={(e) => {
									localTPAStore.drywall1_2_5_8 = e.target.checked;
								}}
							/>
							<div>drywall 1/2 or 5/8</div>
						</div>
						{localTPAStore.drywall1_2_5_8 && (
							<div className="flex justify-between items-center m-1">
								<LineItemBuilderMini
									initialValues={localTPAStore.drywall1_2_5_8_affected_material}
									title={"affected material"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywall1_2_5_8_affected_material = v;
									}}
								/>
								<LineItemBuilderMini
									key={localTPAStore.drywall_continuous_surface.join(",")}
									initialValues={localTPAStore.drywall_continuous_surface}
									title={"continuous surface"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywall_continuous_surface = v;
									}}
									withWidthSelector={true}
									onChangeWidth={(value) => {
										updateDrywallWidthType(value as EDrywallWidthCalculation);
									}}
									widthSelectorValue={localTPAStore?.drywallWidthType}
								/>
							</div>
						)}
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1 justify-start "
								checked={localTPAStore.drywallFloodCuts}

								onChange={(e) => {
									localTPAStore.drywallFloodCuts = e.target.checked;
								}}
							/>
							<div>drywall flood cuts</div>
						</div>
						{localTPAStore.drywallFloodCuts && (
							<div className="flex justify-between items-center m-1">
								<LineItemBuilderMini
									initialValues={localTPAStore.drywallFloodCuts_affected_material}
									title={"affected material"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywallFloodCuts_affected_material = v;
									}}
								/>
								<LineItemBuilderMini
									key={localTPAStore.drywall_continuous_surface.join(".")}
									initialValues={localTPAStore.drywall_continuous_surface}
									title={"continuous surface"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywall_continuous_surface = v;
									}}
									withWidthSelector={true}
									onChangeWidth={(value) => {
										updateDrywallWidthType(value as EDrywallWidthCalculation);
									}}
									widthSelectorValue={localTPAStore?.drywallWidthType}
								/>
							</div>
						)}
						<div className="w-full flex items-center">
							<input
								type="checkbox"
								className="m-1 justify-start "
								checked={localTPAStore.drywallPatchWithSize}
								onChange={(e) => {
									localTPAStore.drywallPatchWithSize = e.target.checked;
								}}
							/>
							<div>drywall patch </div>
							{localTPAStore.drywallPatchWithSize && (
								<input
									type="number"
									className="m-1 w-8 px-1"
									placeholder="4"
									value={localTPAStore.dryWallPatchSize}
									onChange={(e) => {
										localTPAStore.dryWallPatchSize = parseFloat(e.target.value);
									}}
									onBlur={(e) => {
										if (e.target.value === "" || parseFloat(e.target.value) === 0) {
											localTPAStore.dryWallPatchSize = 4;
										}
									}}
								/>
							)}
						</div>
						{localTPAStore.drywallPatchWithSize && (
							<div className="flex justify-between items-center m-1">
								<LineItemBuilderMini
									initialValues={localTPAStore.drywallPatchWithSize_affected_material}
									title={"affected material"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywallPatchWithSize_affected_material = v;
									}}
								/>
								<LineItemBuilderMini
									key={localTPAStore.drywall_continuous_surface.join("-")}
									initialValues={localTPAStore.drywall_continuous_surface}
									title={"continuous surface"}
									elements={lineItemsElements[0].elements}
									onChange={(v) => {
										localTPAStore.drywall_continuous_surface = v;
									}}
									withWidthSelector={true}
									onChangeWidth={(value) => {
										updateDrywallWidthType(value as EDrywallWidthCalculation);
									}}
									widthSelectorValue={localTPAStore?.drywallWidthType}
								/>
							</div>
						)}

						{/* Baseboards */}
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1 justify-start"
								checked={localTPAStore.baseboards}
								onChange={(e) => {
									localTPAStore.baseboards = e.target.checked;
								}}
							/>
							<div>Baseboards (Size)</div>
						</div>
						{localTPAStore.baseboards && (
							<div className="ml-4 mb-4">
								<div className="flex justify-between items-center m-1">
									<LineItemBuilderMini
										initialValues={localTPAStore.baseboards_affected_material}
										title={"affected material"}
										elements={lineItemsElements[1].elements}
										onChange={(v) => {
											localTPAStore.baseboards_affected_material = v;
										}}
										defaultValues={["oversized"]}
									/>
									<LineItemBuilderMini
										key={localTPAStore.baseboards_continuous_surface.join(",")}
										initialValues={localTPAStore.baseboards_continuous_surface}
										title={"continuous surface"}
										elements={lineItemsElements[1].elements}
										withWidthSelector={true}
										onChange={(v) => {
											localTPAStore.baseboards_continuous_surface = v;
										}}
										defaultValues={["oversized"]}
										widthSelectorOptions={Object.values(PFWidthCalculation)}
										widthSelectorValue={localTPAStore?.baseboardsWidthType}
										onChangeWidth={(value) => {
											updateBaseboardsWidthType(value as PFWidthCalculation);
										}}
									/>
								</div>
							</div>
						)}

						{/* Crown Molding */}
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1 justify-start"
								checked={localTPAStore.crownMolding}
								onChange={(e) => {
									localTPAStore.crownMolding = e.target.checked;
								}}
							/>
							<div>Crown Molding (Size)</div>
						</div>
						{localTPAStore.crownMolding && (
							<div className="ml-4 mb-4">
								<div className="text-sm text-gray-600 mb-2">
									<div>Painting Line item: crown paint oversized</div>
									<div>Affected material calculation: inherit from parent</div>
									<div>Continuous surface calculation = PC or PC-affected material</div>
								</div>
								<div className="flex justify-between items-center m-1">
									<LineItemBuilderMini
										initialValues={localTPAStore.crownMolding_affected_material}
										title={"affected material"}
										elements={lineItemsElements[2].elements}
										onChange={(v) => {
											localTPAStore.crownMolding_affected_material = v;
										}}
										defaultValues={["oversized"]}
									/>
									<LineItemBuilderMini
										key={localTPAStore.crownMolding_continuous_surface.join(",")}
										initialValues={localTPAStore.crownMolding_continuous_surface}
										title={"continuous surface"}
										elements={lineItemsElements[2].elements}
										withWidthSelector={true}
										onChange={(v) => {
											localTPAStore.crownMolding_continuous_surface = v;
										}}
										defaultValues={["oversized"]}
										widthSelectorOptions={Object.values(PFWidthCalculation)}
										widthSelectorValue={localTPAStore?.crownMoldingWidthType}
										onChangeWidth={(value) => {
											updateCrownMoldingWidthType(value as PFWidthCalculation);
										}}
									/>
								</div>
							</div>
						)}

						{/* Chair Rail */}
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1 justify-start"
								checked={localTPAStore.chairRail}
								onChange={(e) => {
									localTPAStore.chairRail = e.target.checked;
								}}
							/>
							<div>Chair Rail (Size)</div>
						</div>
						{localTPAStore.chairRail && (
							<div className="ml-4 mb-4">
								<div className="flex justify-between items-center m-1">
									<LineItemBuilderMini
										initialValues={localTPAStore.chairRail_affected_material}
										title={"affected material"}
										elements={lineItemsElements[3].elements}
										onChange={(v) => {
											localTPAStore.chairRail_affected_material = v;
										}}
										defaultValues={["oversized"]}
									/>
									<LineItemBuilderMini
										key={localTPAStore.chairRail_continuous_surface.join(",")}
										initialValues={localTPAStore.chairRail_continuous_surface}
										title={"continuous surface"}
										elements={lineItemsElements[3].elements}
										withWidthSelector={true}
										onChange={(v) => {
											localTPAStore.chairRail_continuous_surface = v;
										}}
										defaultValues={["oversized"]}
										widthSelectorOptions={Object.values(PCWidthCalculation)}
										widthSelectorValue={localTPAStore?.chairRailWidthType}
										onChangeWidth={(value) => {
											updateChairRailWidthType(value as PCWidthCalculation);
										}}
									/>
								</div>
							</div>
						)}

						{/* Trim Board */}
						<div className="w-full flex">
							<input
								type="checkbox"
								className="m-1 justify-start"
								checked={localTPAStore.trimBoard}
								onChange={(e) => {
									localTPAStore.trimBoard = e.target.checked;
								}}
							/>
							<div>Trim board (size)</div>
						</div>
						{localTPAStore.trimBoard && (
							<div className="ml-4 mb-4">
								<div className="text-sm text-gray-600 mb-2">
									<div>Painting Line item: Trim paint one coat</div>
									<div>Affected material calculation: inherit from parent</div>
									<div>Continuous surface calculation = PF or PF- affected material</div>
								</div>
								<div className="flex justify-between items-center m-1">
									<LineItemBuilderMini
										initialValues={localTPAStore.trimBoard_affected_material}
										title={"affected material"}
										defaultValues={["oversized"]}
										elements={lineItemsElements[4].elements}
										onChange={(v) => {
											localTPAStore.trimBoard_affected_material = v;
										}}
									/>
									<LineItemBuilderMini
										key={localTPAStore.trimBoard_continuous_surface.join(",")}
										initialValues={localTPAStore.trimBoard_continuous_surface}
										title={"continuous surface"}
										elements={lineItemsElements[4].elements}
										onChange={(v) => {
											localTPAStore.trimBoard_continuous_surface = v;
										}}
										defaultValues={["oversized"]}
										withWidthSelector={true}
										widthSelectorOptions={Object.values(PFWidthCalculation)}
										widthSelectorValue={localTPAStore?.trimBoardWidthType}
										onChangeWidth={(value) => {
											updateTrimBoardWidthType(value as PFWidthCalculation);
										}}
									/>
								</div>
							</div>
						)}

						<br></br>
						<br></br>
					</div>
				)}
		</div>
	);
});

const RolledFlooringWasteCalculationScreen: React.FC<{}> = observer(() => {
	const appliedRules = [
		"Floors vinyl sheet goods, per sf asbestos high grade remove",
		"floors Carpet, per sf high replace pattern",
		"Floors carpet Pad, per sf replace high grade 3/8",
	];

	return (
		<div className="p-2 flex flex-col">
			<span>
				<input
					className="mr-2"
					onChange={() => {
						localTPAStore.rollupType = "";
					}}
					type="radio"
					checked={!localTPAStore.rollupType}
				/>
				None
			</span>
			<span>
				<input
					className="mr-2"
					onChange={() => {
						localTPAStore.rollupType = "FLR_RPL";
					}}
					type="radio"
					checked={localTPAStore.rollupType === "FLR_RPL"}
				/>
				FLR_RPL
			</span>
			<span className="flex">
				<input
					className="mr-2"
					onChange={() => {
						localTPAStore.rollupType = "Percentage";
					}}
					type="radio"
					checked={localTPAStore.rollupType === "Percentage"}
				/>
				<span className="mr-2">Extra Waste Percentage</span>
				{localTPAStore.rollupType === "Percentage" && (
					<div className="flex">
						<input
							type="number"
							value={localTPAStore.rollupPercentage}
							onChange={(e) => {
								localTPAStore.rollupPercentage = parseFloat(e.target.value);
							}}
							className="w-20 px-1"
						/>
					</div>
				)}
			</span>
			{appliedRules.map((r) => {
				return (
					<span>
						<input
							checked={localTPAStore.rollupApplied.includes(r)}
							onChange={() => {
								if (localTPAStore.rollupApplied.includes(r)) {
									localTPAStore.rollupApplied = localTPAStore.rollupApplied.filter((rule) => rule !== r);
								} else {
									localTPAStore.rollupApplied = [...localTPAStore.rollupApplied, r];
								}
							}}
							type="checkbox"
							className="mr-2"
						/>

						{r}
					</span>
				);
			})}
		</div>
	);
});

const AlternativeMaximumScreen: React.FC<{}> = observer(() => {
	return (
		<div className="h-full overflow-auto m-2">
			{localTPAStore.allWithMaxQuantity.map((v) => {
				// logDebug("allWithMaxQuantity", v);
				return (
					<div key={v.tupleID}>
						<span className="mb-2">{v.tupleID}</span>
						{Object.keys(v.maxQuantity).map((measurment) => {
							return (
								<div key={measurment} className="flex items-start mt-2">
									<span>{measurment}</span>
									<input
										className="px-1 mx-2"
										type="number"
										value={localTPAStore.alternativeMaximums.find((a) => a.tupleID === v.tupleID)?.maxQuantity || ""}
										placeholder={v.maxQuantity[measurment].toFixed()}
										onChange={(e) => {
											localTPAStore.setMaxQuantity(v.tupleID, measurment, parseFloat(e.target.value));
										}}
									/>
									<textarea
										className="px-1 mx-2 w-[400px]"
										placeholder={"Type explanation"}
										value={localTPAStore.alternativeMaximums.find((a) => a.tupleID === v.tupleID)?.explanation || ""}
										onChange={(e) => {
											localTPAStore.setExplanation(v.tupleID, e.target.value);
										}}
									/>
								</div>
							);
						})}
					</div>
				);
			})}
		</div>
	);
});

const AlternateXactimateCodingScreen: React.FC<{}> = observer(() => {
	return (
		<div className="h-full overflow-auto w-full">
			<div className="flex w-full justify-end">
				<button
					onClick={() => {
						localTPAStore.alternativeXAMappings = [...localTPAStore.alternativeXAMappings, { source: "", dest: "" }];
					}}
					className="rounded bg-blue-500 text-white px-1 mr-2 mt-2"
				>
					Add Mapping
				</button>
			</div>
			<div>
				{localTPAStore.alternativeXAMappings.map((mapping, index) => {
					return (
						<div key={index} className="flex items-start mt-2">
							<input
								type="text"
								placeholder="source"
								onChange={(e) => {
									localTPAStore.setAlternativeXAMappingsSource(index, e.target.value);
								}}
								value={mapping.source}
								className="px-1 mx-2"
							/>
							<span>→</span>
							<input
								onChange={(e) => {
									localTPAStore.setAlternativeXAMappingsDest(index, e.target.value);
								}}
								type="text"
								placeholder="dest"
								value={mapping.dest}
								className="px-1 mx-2"
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
});

const ConRoomToConLabScreen: React.FC<{}> = observer(() => {

	return (
		<div className="p-4 flex flex-col gap-6">
			<div>
				<h3 className="text-lg font-semibold mb-3">Convert to</h3>
				<div className="flex flex-col gap-2">
					<label className="flex items-center gap-2">
						<input
							type="radio"
							name="conversionType"
							value=""
							checked={localTPAStore.conRoomToConLabType === ""}
							onChange={(e) => localTPAStore.conRoomToConLabType = e.target.value}
							className="w-4 h-4"
						/>
						<span>None</span>
					</label>
					<label className="flex items-center gap-2">
						<input
							type="radio"
							name="conversionType"
							value="CON_LAB"
							checked={localTPAStore.conRoomToConLabType === "CON_LAB"}
							onChange={(e) => localTPAStore.conRoomToConLabType = e.target.value}
							className="w-4 h-4"
						/>
						<span>CON LAB</span>
					</label>
					<label className="flex items-center gap-2">
						<input
							type="radio"
							name="conversionType"
							value="CON_ROOM"
							checked={localTPAStore.conRoomToConLabType === "CON_ROOM"}
							onChange={(e) => localTPAStore.conRoomToConLabType = e.target.value}
							className="w-4 h-4"
						/>
						<span>CON ROOM</span>
					</label>
				</div>
			</div>

			<div className="mt-4 p-3 bg-gray-100 rounded-md">
				<p className="text-sm text-gray-700">
					<strong>Selected Configuration:</strong>
				</p>
				<p className="text-sm text-gray-600">
					Type: {localTPAStore.conRoomToConLabType === "" ? "None" :
						localTPAStore.conRoomToConLabType === "CON_LAB" ? "CON LAB" : "CON ROOM"} |
				</p>
			</div>
		</div>
	);
});

const NewRuleScreen: React.FC = observer(() => {
	useEffect(() => {
		localTPAStore.fetchComplementes();
	}, []);
	if (localTPAStore.allActiveComplements.length === 0) {
		return <div>Loading...</div>;
	}
	function getScreen() {
		switch (localTPAStore.newRuleType) {
			case TPARuleTypes.RemoveComplement:
				return <TPAComplementRules />;
			case TPARuleTypes.ConvertToLikelyComplement:
				return <TPAComplementRules />;
			case TPARuleTypes.AdditionalComplements:
				return <TPAComplementRules />;
			case TPARuleTypes.SplitUpLineItems:
				return <SplitUpLineItemsScreen />;
			case TPARuleTypes.RolledFlooringWasteCalculation:
				return <RolledFlooringWasteCalculationScreen />;
			case TPARuleTypes.AlternativeMaximum:
				return <AlternativeMaximumScreen />;
			case TPARuleTypes.AlternateXactimateCoding:
				return <AlternateXactimateCodingScreen />;
			case TPARuleTypes.ConRoomToConLab:
				return <ConRoomToConLabScreen />;
			default:
				return <div>Select a rule type to add</div>;
		}
	}
	return (
		<div className="h-full flex flex-col ">
			<div>
				<div>{localTPAStore.perTpaAndCarrierRules.length > 0 ? "Current TPA/Carrier Rules" : "No Rules yet for TPA/Carrier"}</div>
				<div className="flex w-full overflow-auto">
					{localTPAStore.perTpaAndCarrierRules.map((rule, index) => {
						// logDebug(
						// 	"rule",
						// 	rule,
						// 	typeof rule.tpa,
						// 	typeof rule.carrier,
						// 	localTPAStore.tpa,
						// 	localTPAStore.carrier,
						// 	rule.tpa === localTPAStore.tpa,
						// 	rule.carrier === localTPAStore.carrier
						// );
						if (rule.tpa === localTPAStore.tpa && rule.carrier === localTPAStore.carrier) {
							return (
								<div
									key={index}
									className={`btn text-xs m-1 rounded bg-blue-500 text-white px-1 ${rule.ruleType === localTPAStore.newRuleType ? "bg-blue-700 border-2" : ""
										}`}
									onClick={() => {
										localTPAStore.loadRule(rule);
									}}
								>
									{rule.ruleType}
								</div>
							);
						}
					})}
				</div>
			</div>
			<div className="h-full flex flex-col">
				<div className="border-2 w-full flex justify-between">
					<div>{localTPAStore.newRuleType}</div>
					<div
						className="btn bg-green-500 text-white px-1"
						onClick={() => {
							localTPAStore.saveCurrentRule();
						}}
					>
						Save
					</div>
				</div>
				<div className="bg-blue-200 h-full">{getScreen()}</div>
			</div>
		</div>
	);
});

export { NewRuleScreen };

export const localTPAStore = new LocalTPAStore();
